import { Injectable, OnDestroy } from '@angular/core';

import { BehaviorSubject } from 'rxjs';
import { datadogRum } from '@datadog/browser-rum';

import { TAB_LOCATION_HEADER, TAB_NO_LOCATION, TAB_USER_HEADER } from '@shared/signup/constants';
import { LocalStorageService } from '@tablesready/frontend-storage';
import { ACTIVE_TOKEN_STORAGE_KEY, TOKENS_STORAGE_KEY } from './token.service';

export interface TabIdentity {
  userId: string;
  locationId: string | null;
}

export type TabLockReason = 'request' | 'server' | 'storage';

export interface TabLock {
  reason: TabLockReason;
  signedOut: boolean;
}

type SharedSession =
  { state: 'refreshing' } | { state: 'signedIn'; identity: TabIdentity } | { state: 'signedOut' };

const BASE64_BLOCK_LENGTH = 4;

function readClaim(claims: unknown, claimName: string): string | null {
  if (typeof claims !== 'object' || claims === null) {
    return null;
  }

  const value: unknown = Reflect.get(claims, claimName);

  return typeof value === 'string' && value ? value : null;
}

/** Reads the `user` and `id` (location) claims. No signature check: the server does that. */
export function decodeTabIdentity(token: string | null | undefined): TabIdentity | null {
  const payload = token?.split('.')[1];
  if (!payload) {
    return null;
  }

  try {
    const base64 = payload.replace(/-/gu, '+').replace(/_/gu, '/');
    const blocks = Math.ceil(base64.length / BASE64_BLOCK_LENGTH);
    const padded = base64.padEnd(blocks * BASE64_BLOCK_LENGTH, '=');
    const claims: unknown = JSON.parse(atob(padded));
    const userId = readClaim(claims, 'user');
    if (!userId) {
      return null;
    }

    return {
      userId,
      locationId: readClaim(claims, 'id'),
    };
  } catch {
    return null;
  }
}

function sameIdentity(a: TabIdentity, b: TabIdentity): boolean {
  return a.userId === b.userId && a.locationId === b.locationId;
}

/**
 * All tabs in a browser share one login. This remembers the login THIS tab is showing and
 * locks the tab once the shared login moves to another user or location, so it never sends
 * the new login's token with the old screen's data.
 */
@Injectable({ providedIn: 'root' })
export class TabIdentityService implements OnDestroy {
  readonly lock$ = new BehaviorSubject<TabLock | null>(null);
  private identity: TabIdentity | null = null;

  // The `storage` event fires only in OTHER tabs, so it reports exactly the changes this tab did not make.
  private readonly onStorage = (storageEvent: StorageEvent): void => {
    const { key } = storageEvent;
    if (key === null || key === ACTIVE_TOKEN_STORAGE_KEY || key === TOKENS_STORAGE_KEY) {
      this.checkSharedLogin();
    }
  };

  constructor(private readonly localStorageService: LocalStorageService) {
    const shared = this.readShared();
    this.identity = shared.state === 'signedIn' ? shared.identity : null;
    window.addEventListener('storage', this.onStorage);
  }

  ngOnDestroy(): void {
    window.removeEventListener('storage', this.onStorage);
  }

  /** Call after THIS tab changes the login or location (login, logout, switching location). */
  adopt(): void {
    if (this.isLocked()) {
      return;
    }

    const shared = this.readShared();
    this.identity = shared.state === 'signedIn' ? shared.identity : null;
  }

  /** After a `tokenRevoked` event: this tab only moves when its own location was revoked. */
  adoptIfLocationRevoked(locationIds: string[]): void {
    if (this.identity?.locationId && locationIds.includes(this.identity.locationId)) {
      this.adopt();
    }
  }

  /** Whether `token` may be sent from this tab. Locks the tab and returns false when it may not. */
  verifyToken(token: string, reason: TabLockReason): boolean {
    if (this.isLocked()) {
      return false;
    }

    const tokenIdentity = decodeTabIdentity(token);
    if (!tokenIdentity) {
      return true;
    }

    if (!this.identity) {
      // A tab with no login yet (login page, or booted while the token was refreshing) takes the first one.
      this.identity = tokenIdentity;

      return true;
    }

    if (sameIdentity(this.identity, tokenIdentity)) {
      return true;
    }

    this.lock(reason, false, tokenIdentity);

    return false;
  }

  lock(reason: TabLockReason, signedOut = false, shared: TabIdentity | null = null): void {
    if (this.isLocked()) {
      return;
    }

    this.lock$.next({
      reason,
      signedOut,
    });
    datadogRum.addAction('stale_tab_locked', {
      reason,
      signedOut,
      tabUserId: this.identity?.userId ?? null,
      tabLocationId: this.identity?.locationId ?? null,
      sharedUserId: shared?.userId ?? null,
      sharedLocationId: shared?.locationId ?? null,
    });
  }

  isLocked(): boolean {
    return this.lock$.value !== null;
  }

  /** A copy of the login this tab shows now, or null before it has one. */
  snapshot(): TabIdentity | null {
    return this.identity ? { ...this.identity } : null;
  }

  /** Whether this tab still shows the login and location of `snapshot`; a null snapshot always does. */
  isStillShowing(snapshot: TabIdentity | null): boolean {
    if (!snapshot) {
      return true;
    }

    return this.identity !== null && sameIdentity(this.identity, snapshot);
  }

  /** Identity headers the backend checks against the token; empty until the tab has a login. */
  headers(): Record<string, string> {
    if (!this.identity) {
      return {};
    }

    return {
      [TAB_USER_HEADER]: this.identity.userId,
      [TAB_LOCATION_HEADER]: this.identity.locationId ?? TAB_NO_LOCATION,
    };
  }

  /** Locks if another tab changed the shared login, without waiting for that tab's `storage` event. */
  checkSharedLogin(): void {
    if (this.isLocked() || !this.identity) {
      return;
    }

    const shared = this.readShared();
    if (shared.state === 'refreshing') {
      // The active token expired and was dropped; the refresh tokens remain. Not a logout.
      return;
    }

    if (shared.state === 'signedOut') {
      this.lock('storage', true);

      return;
    }

    if (!sameIdentity(this.identity, shared.identity)) {
      this.lock('storage', false, shared.identity);
    }
  }

  private readShared(): SharedSession {
    // Raw read: TokenService.getActive() would drop an expired active token as a side effect.
    const active = this.localStorageService.get<{ token?: string }>(ACTIVE_TOKEN_STORAGE_KEY);
    const identity = decodeTabIdentity(active?.token);
    if (identity) {
      return {
        state: 'signedIn',
        identity,
      };
    }

    const tokens = this.localStorageService.get<unknown[]>(TOKENS_STORAGE_KEY);

    return Array.isArray(tokens) && tokens.length > 0 ? { state: 'refreshing' } : { state: 'signedOut' };
  }
}
